// src/services/simulationService.js
// --- UPDATE: v1.0 — client for /api/v1/simulations (backend/routes/simulationRoutes.js)
//   runSimulation   POST /simulations/run   (all 7 scenarios incl. hurricane + flood forecast)
//   getRun / listRuns                        re-open recent runs (6 h registry)
//   getStormOutlook GET  /simulations/outlook live weather / storm signal for the network
//   getSimulationStatus                      engine + topology + weather cache stats
// Errors are normalised to Error objects carrying { status, code, retryAfter }.
import api from './api';

const STORM_TIMEOUT_MS = 180000;

const normaliseError = (err) => {
  if (err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
    const e = new Error('Simulation cancelled');
    e.cancelled = true;
    return e;
  }
  const body = err?.response?.data || {};
  const e = new Error(body.error || body.message || err?.message || 'Simulation request failed');
  e.status = err?.response?.status ?? null;
  e.code = body.code || null;
  e.details = body.details || null;
  e.retryAfter = Number(err?.response?.headers?.['retry-after']) || null;
  return e;
};

/**
 * @param {{ scenario: string, assetId?: string|number, parameters?: object }} request
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<{ run_id, scenario, title, created_at, duration_ms, data }>}
 */
export async function runSimulation({ scenario, assetId = null, parameters = {} }, { signal } = {}) {
  try {
    const res = await api.post('/simulations/run', { scenario, assetId, parameters }, { signal, timeout: STORM_TIMEOUT_MS });
    const body = res?.data || {};
    if (body.success === false) throw Object.assign(new Error(body.error || 'Simulation failed'), { code: body.code });
    return body;
  } catch (err) {
    throw normaliseError(err);
  }
}

export async function getRun(runId, { limit } = {}) {
  try {
    const res = await api.get(`/simulations/runs/${encodeURIComponent(runId)}`, { params: limit ? { limit } : undefined });
    return res?.data;
  } catch (err) {
    throw normaliseError(err);
  }
}

export async function listRuns({ scenario } = {}) {
  try {
    const res = await api.get('/simulations/runs', { params: scenario ? { scenario } : undefined });
    return res?.data?.data || [];
  } catch (err) {
    throw normaliseError(err);
  }
}

export async function getStormOutlook({ lat, lng, hours = 72, signal } = {}) {
  try {
    const params = { hours };
    if (lat != null && lng != null) { params.lat = lat; params.lng = lng; }
    const res = await api.get('/simulations/outlook', { params, signal, timeout: 20000 });
    return res?.data?.data || null;
  } catch (err) {
    throw normaliseError(err);
  }
}

export async function getSimulationStatus() {
  try {
    const res = await api.get('/simulations/status');
    return res?.data?.data || null;
  } catch (err) {
    throw normaliseError(err);
  }
}

const simulationService = { runSimulation, getRun, listRuns, getStormOutlook, getSimulationStatus };
export default simulationService;
// --- END UPDATE